import { useCallback, useEffect, useMemo, useState } from 'react';
import { CountySelectComponent } from '~/components/dropdowns/county/county-select.component';
import { HouseholdOccupancySelectComponent } from '~/components/dropdowns/house-hold-occupancy/household-occupancy-select.component';
import { Container } from '~/components/layout';

import { LmiReviewContext } from '~/machines/lmi-review';
import { SelfAttestationData } from '~/screens/vnm-terms';
import { MnLmiCountyIncomeLimits } from './county-income-limits-mn';

interface Props {
  context: LmiReviewContext;
  onFormUpdate: (data: Omit<SelfAttestationData, 'lmiProgram'>) => void;
  defaultCounties: ReadonlyArray<string>;
}

export const MAX_HOUSEHOLD_OCCUPANCY_LIMIT = 8;

export const SelfAttestation = ({ context, onFormUpdate, defaultCounties }: Props) => {
  const { zipCode, id: userId } = context.funnelPacket.user;

  const houseOccupancyOptions = useMemo(() => {
    const options: Array<{
      disabled: boolean;
      selected: boolean;
      text: string;
      value: number;
    }> = [];
    for (let i = 1; i <= MAX_HOUSEHOLD_OCCUPANCY_LIMIT; i++) {
      options[i] = {
        disabled: false,
        selected: false,
        text: `${i > 1 ? `${i} Persons` : `${i} Person`}`,
        value: i,
      };
    }
    return options;
  }, []);

  const [formData, setFormData] = useState({
    county: '',
    houseOccupancy: '',
  });

  const [selfAttestationData, setSelfAttestationData] = useState({
    county: '',
    houseOccupancy: '',
    houseIncome: '',
  });

  const handleOnChange = useCallback((fieldName: keyof typeof formData, value) => {
    setFormData(prevState => {
      return {
        ...prevState,
        [fieldName]: value,
      };
    });
  }, []);

  useEffect(() => {
    const houseOccupancy = formData.houseOccupancy
      ? String(houseOccupancyOptions[Number(formData.houseOccupancy)].value)
      : '';
    const houseIncome =
      formData.houseOccupancy &&
      MnLmiCountyIncomeLimits.getIncomeLimits(
        formData.county.toLowerCase().split('county')[0].trim(),
        Number(formData.houseOccupancy) - 1,
      );
    setSelfAttestationData({
      county: formData.county,
      houseOccupancy,
      houseIncome,
    });
  }, [formData]);

  useEffect(() => {
    onFormUpdate(selfAttestationData);
  }, [selfAttestationData]);
  return (
    <Container addInputMargin>
      <CountySelectComponent
        zipCode={zipCode!}
        defaultCounties={defaultCounties}
        userId={userId}
        onChange={value => {
          handleOnChange('county', value);
        }}
        value={formData.county}
      />
      <HouseholdOccupancySelectComponent
        onChange={value => handleOnChange('houseOccupancy', houseOccupancyOptions[value].value)}
        options={houseOccupancyOptions}
        value={formData.houseOccupancy}
      />
    </Container>
  );
};
